/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PencilIcon, EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { LuRefreshCcw, LuCircleCheck } from "react-icons/lu";
import axios from 'axios';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import { ThumbsUpIcon } from '../../../components/icons/ThumbsUpIcon';
import { ThumbsDownIcon } from '../../../components/icons/ThumbsDownIcon';
import { CopyIcon } from '../../../components/icons/CopyIcon';
import PDFFilePreview from './PDFFilePreview';
import ImageModal from '../../../components/common/ImageModal';
import PresentationCard from '../../../components/common/PresentationCard';
import AIPresentationCard from '../../../components/common/AIPresentationCard';
import HtmlChartArtifact from './ChatMessage/components/HtmlChartArtifact';
import ArtifactViewCard from './ChatMessage/components/ArtifactViewCard';

import '../styles/chat.css';
import { processMarkdownWithInlineTools } from '../utils/inlineToolRenderer';
import { useMessageStatus } from '../hooks/useMessageStatus';
import UnifiedLoadingIndicator from './UnifiedLoadingIndicator';

// Import extracted components
import {
  AuthenticatedImage,
  CodeBlock,
  DocumentAnalysisIndicator,
  FeedbackForm,
  FileAttachment,
  GracefulStreamingMarkdown,
  ImageGenerationSkeleton,
  SearchIndicator,
  ShimmerText,
  StreamingMarkdown,
  UserProfileHeader
} from './ChatMessage/components';

// Import indicators
import MemoryIndicator from './indicators/MemoryIndicator';
import ViewMemoryButton from './buttons/ViewMemoryButton';

// Import extracted hooks
import {
  // useBranding, // uncomment with bot logo circle below
  useCopy,
  useFeedback,
  useResponseSwitching,
  useSearchHighlighting
} from './ChatMessage/hooks';

// Import extracted utilities
import {
  getFaviconUrl,
  hasFileLinks,
  extractMarkdownLink,
  getUserInfo,
  getUserColor,
  isPDFFile,
  getProxiedImageUrl,
  isImageFile
} from './ChatMessage/utils';

const supportsArtifactPanelPreview = (generatedFile) => {
  const format = generatedFile?.format?.toLowerCase?.() || '';
  const fileName = generatedFile?.fileName?.toLowerCase?.() || '';
  return format === 'html' || format === 'pdf' || fileName.endsWith('.html') || fileName.endsWith('.pdf');
};





const deriveMentionHandle = (member, fallback = 'member') => {
  if (!member) return fallback;
  const handleSource = member.username
    || member.handle
    || member.name
    || member.fullName
    || member.displayName
    || member.email
    || member.phoneNumber
    || fallback;
  return handleSource.replace(/\s+/g, '');
};

const normalizeId = (value) => {
  if (!value) return null;
  if (typeof value === 'string' || typeof value === 'number') return value.toString();
  if (value._id) return value._id.toString();
  if (typeof value.toString === 'function') return value.toString();
  return null;
};

const EMPTY_SENT_DRAFTS = [];

const ChatMessage = ({ 
  message, 
  chatId,
  chatTitle,
  handleCopy, 
  handleEdit, 
  handleReprompt,
  handleFeedbackSubmit,
  editingMessageIndex,
  setEditingMessageIndex,
  editInput,
  setEditInput,
  searchQuery,
  isActiveSearchResult,
  messages,
  projectData,
  onDraftSent,
  onUpdateMessageContent,
  currentUser,
  toolCallManager,
  isOwnMessage,
  hideUserHeader = false,
  compactUserMessage = false,
  onOpenArtifact
}) => {
  const resolvedIsOwnMessage = typeof isOwnMessage === 'boolean'
    ? isOwnMessage
    : normalizeId(message?.userId) === normalizeId(currentUser?.id);
  const { darkMode } = useTheme();
  const [showContinueButton, setShowContinueButton] = useState(false);

  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedImageAlt, setSelectedImageAlt] = useState('');
  
  // State for handling generated image loading errors (fallback to proxy)
  const [imageError, setImageError] = useState(false);
  const [currentResponseImageError, setCurrentResponseImageError] = useState(false);

  // Container ref for content section utilities
  const messageContentRef = useRef(null);

  const rawMessageId = message?._id;
  const messageIsTemporary = Boolean(message?.isTemporary);

  const stableMessageId = useMemo(() => {
    if (!rawMessageId || messageIsTemporary) {
      return null;
    }

    const idString = typeof rawMessageId === 'string'
      ? rawMessageId
      : (typeof rawMessageId?.toString === 'function' ? rawMessageId.toString() : null);

    if (!idString) {
      return null;
    }

    if (idString.startsWith('temp-')) {
      return null;
    }

    return idString;
  }, [rawMessageId, messageIsTemporary]);

  const canSubmitFeedback = useMemo(() => {
    if (!chatId || chatId.toString().startsWith('a-')) {
      return false;
    }
    return Boolean(stableMessageId);
  }, [chatId, stableMessageId]);

  // Custom hooks
  // const { logoUrl } = useBranding(darkMode); // uncomment with bot logo circle block below
  const { isCopied, handleCopy: handleLocalCopy } = useCopy();
  const { highlightedContent, hasHighlights } = useSearchHighlighting(message, messages, searchQuery, isActiveSearchResult);
  const [sendingDraft, setSendingDraft] = useState(false);

  const handleSendDraft = useCallback(async (draft) => {
    if (!chatId || !draft?.to || !draft?.subject || (draft.source !== 'outlook' && draft.source !== 'gmail')) return;
    setSendingDraft(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const res = await axios.post(`${apiUrl}/chat/${chatId}/send-draft-email`, {
        to: draft.to,
        subject: draft.subject,
        body: draft.body || '',
        source: draft.source,
        messageId: message?._id
      }, { headers: { Authorization: `Bearer ${token}` } });
      const toStr = draft.to || '';
      const toEmail = toStr.includes('<') ? (toStr.match(/<([^>]+)>/) || [])[1]?.trim() || toStr : toStr.trim();
      onDraftSent?.(message?._id, { to: toEmail, subject: draft.subject, sentAt: res.data.sentAt });
    } catch (err) {
      console.error('Send draft failed:', err);
      alert(err.response?.data?.error || 'Failed to send email. Please try again.');
      throw err;
    } finally {
      setSendingDraft(false);
    }
  }, [chatId, message?._id, onDraftSent]);

  const sentDrafts = message?.metadata?.sentDrafts ?? EMPTY_SENT_DRAFTS;
  
  const userMentionColorHex = useMemo(() => {
    if (message?.sender !== 'user') return null;
    const ownerId = normalizeId(message?.userId);
    if (!ownerId) return null;
    const colorData = getUserColor(ownerId);
    if (typeof colorData === 'string') return colorData;
    return colorData?.hex || null;
  }, [message?.sender, message?.userId]);

  const projectMentionColorMap = useMemo(() => {
    const map = {};
    if (Array.isArray(projectData?.members)) {
      projectData.members.forEach((member) => {
        const handle = deriveMentionHandle(member);
        if (!handle) return;
        const colorSource = member.userColor
          ? { hex: member.userColor }
          : getUserColor(member.id || member._id || member.userId || handle);
        const colorHex = typeof colorSource === 'string' ? colorSource : colorSource?.hex;
        if (colorHex) {
          map[`@${handle.toLowerCase()}`] = colorHex;
        }
      });
    }
    return map;
  }, [projectData]);

  const getMentionColor = useCallback((mention) => {
    if (!mention) return null;
    const lower = mention.toLowerCase();
    if (lower === '@midus') {
      return userMentionColorHex;
    }
    return projectMentionColorMap[lower] || null;
  }, [projectMentionColorMap, userMentionColorHex]);

  const renderContentWithMentions = useCallback((text) => {
    if (!text) return null;
    const parts = text.split(/(@[A-Za-z0-9._-]+)/g);
    return parts.map((part, idx) => {
      if (!part) return null;
      if (part.startsWith('@')) {
        const color = getMentionColor(part);
        if (color) {
            return (
              <span
              key={`mention-${idx}`}
              className="font-semibold"
              style={{ color }}
              >
              {part}
              </span>
            );
          }
      }
      return <React.Fragment key={`text-${idx}`}>{part}</React.Fragment>;
    });
  }, [getMentionColor]);

  const renderUserTextWithMentions = useCallback((text) => {
    if (!text) {
      return null;
    }

    const lines = text.split('\n');
    return lines.map((line, lineIndex) => (
      <React.Fragment key={`user-line-${lineIndex}`}>
        {renderContentWithMentions(line)}
        {lineIndex < lines.length - 1 && <br key={`user-break-${lineIndex}`} />}
      </React.Fragment>
    ));
  }, [renderContentWithMentions]);
  
  const { currentResponse, activeResponseIndex, hasMultipleResponses, handlePreviousResponse, handleNextResponse } = useResponseSwitching(message);

  const currentMessageText = currentResponse?.text ?? message?.text ?? '';
  const responseIndex = (message?.responses && message.responses.length > 0) ? activeResponseIndex : undefined;

  const handleSaveDraftContent = useCallback(async (rawContent, draft, currentText, respIndex, codeBlockLang = '') => {
    if (!chatId || !message?._id || !onUpdateMessageContent) return;
    const lines = [];
    if (draft.to) lines.push(`To: ${draft.to}`);
    if (draft.from) lines.push(`From: ${draft.from}`);
    if (draft.subject) lines.push(`Subject: ${draft.subject}`);
    lines.push('');
    lines.push(draft.body || '');
    const newContent = lines.join('\n');
    const text = (currentText || '').replace(/\r\n/g, '\n');
    const normalizedRaw = (rawContent || '').replace(/\r\n/g, '\n').trim();
    // Replace the entire fenced code block (opening + content + closing) so we don't pull in
    // surrounding text when the bot didn't close the block, or corrupt the structure.
    const lang = (codeBlockLang || '').trim();
    const blockRe = /```(?:\w+)?\s*\n([\s\S]*?)\n```/g;
    let newText = text;
    let found = false;
    let m;
    while ((m = blockRe.exec(text)) !== null) {
      const blockContent = m[1].replace(/\r\n/g, '\n').trim();
      const contentMatches = blockContent === normalizedRaw ||
        blockContent.startsWith(normalizedRaw) ||
        (normalizedRaw.startsWith(blockContent) && blockContent.length > 50);
      if (contentMatches) {
        const fullBlock = m[0];
        const langMatch = fullBlock.match(/^```(\w+)?\s*\n/);
        const actualLang = (langMatch && langMatch[1]) ? langMatch[1] : (lang || '');
        const replacement = '```' + (actualLang ? actualLang + ' ' : '') + '\n' + newContent + '\n```';
        newText = text.replace(fullBlock, replacement);
        found = true;
        break;
      }
    }
    if (!found) {
      newText = text.replace(normalizedRaw, newContent);
    }
    if (newText === text) return;
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      await axios.patch(
        `${apiUrl}/chat/${chatId}/messages/${message._id}/content`,
        { text: newText, responseIndex: respIndex ?? 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdateMessageContent(message._id, newText, respIndex);
    } catch (err) {
      console.error('Save draft failed:', err);
      alert(err.response?.data?.error || 'Failed to save. Please try again.');
    }
  }, [chatId, message?._id, onUpdateMessageContent]);

  // Helper function to choose the right markdown component based on streaming state
  const MarkdownComponent = useCallback(({ isStreaming, ...props }) => {
    const markdownProps = {
      ...props,
      onSendDraft: handleSendDraft,
      sendDraftLoading: sendingDraft,
      sentDrafts,
      onOpenArtifact,
      messageId: message?._id,
      chatId,
      onDraftSent,
      onSaveDraftContent: handleSaveDraftContent,
      currentMessageText,
      responseIndex,
    };
    return isStreaming ? 
      <GracefulStreamingMarkdown {...markdownProps} isStreaming={isStreaming} /> :
      <StreamingMarkdown {...markdownProps} isStreaming={isStreaming} />;
  }, [handleSendDraft, sendingDraft, sentDrafts, onOpenArtifact, message, chatId, onDraftSent, handleSaveDraftContent, currentMessageText, responseIndex]);
  
  // Feedback hook
  const {
    feedback,
    showNegativeFeedbackForm,
    feedbackText,
    setFeedbackText,
    feedbackScreenshot,
    isSubmittingFeedback,
    isTakingScreenshot,
    feedbackFormRef,
    handleFeedback,
    handleDetailedFeedbackSubmit,
    handleFeedbackCancel,
    handleCloseWithoutSubmit,
    handleScreenshotUpload,
    handleTakeScreenshot
  } = useFeedback(message, chatId, handleFeedbackSubmit);
  
  // Add unified loading system
  const { getLoadingIndicator, isMessageLoading } = useMessageStatus();

  // Check if this is a project chat
  const isProjectChat = Boolean(projectData?.id);

  // Memoize search indicator data to prevent infinite re-renders
  const searchIndicatorData = useMemo(() => {
    const messageHasSearch = message?.isSearching || message?.hasWebSearch || message?.hasSearched;
    const responseHasSearch = currentResponse?.isSearching || currentResponse?.hasWebSearch || currentResponse?.hasSearched;
    const messageHasSearchData = message?.searchResults?.length > 0 || message?.searchSources?.length > 0;
    const responseHasSearchData = currentResponse?.searchResults?.length > 0 || currentResponse?.searchSources?.length > 0;
    const messageHasHubSpot = message?.hubspotFunction || message?.hasHubSpotFunction;
    const responseHasHubSpot = currentResponse?.hubspotFunction || currentResponse?.hasHubSpotFunction;
    
    const shouldShow = (messageHasSearch || responseHasSearch || messageHasSearchData || responseHasSearchData || messageHasHubSpot || responseHasHubSpot);
    
    if (!shouldShow) return null;
    
    return {
      ...message,
      ...currentResponse,
      searchResults: message?.searchResults || currentResponse?.searchResults || [],
      searchSources: message?.searchSources || currentResponse?.searchSources || [],
      hasWebSearch: message?.hasWebSearch || currentResponse?.hasWebSearch || false,
      isSearching: message?.isSearching || currentResponse?.isSearching || false
    };
  }, [
    message?.searchResults, 
    message?.searchSources, 
    message?.hasWebSearch, 
    message?.isSearching,
    message?.hasSearched,
    message?.text,
    message?.hubspotFunction,
    message?.hasHubSpotFunction,
    currentResponse?.searchResults, 
    currentResponse?.searchSources, 
    currentResponse?.hasWebSearch, 
    currentResponse?.isSearching,
    currentResponse?.hasSearched,
    currentResponse?.text,
    currentResponse?.searchStatus,
    currentResponse?.hubspotFunction,
    currentResponse?.hasHubSpotFunction
  ]);

  // Check if this message potentially was cut off
  useEffect(() => {
    // Only show continue button if the message is from the bot and is marked as truncated
    setShowContinueButton(message?.sender === "bot" && message?.isTruncated === true);
  }, [message]);

  const handleContinueResponse = () => {
    // If this is a bot message and continue button is visible
    if (message?.sender === "bot" && showContinueButton) {
      // Create a special prompt that indicates we want to continue this message
      const continueText = "Please continue where you left off, appending to your previous response";
      if (handleReprompt) {
        // Pass true as third parameter to indicate this is a continue operation
        handleReprompt(message, continueText, true);
      }
    }
  };

  // Handle image modal
  const handleImageClick = (imageUrl, imageAlt) => {
    setSelectedImageUrl(imageUrl);
    setSelectedImageAlt(imageAlt);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImageUrl('');
    setSelectedImageAlt('');
  };

  const renderContent = () => {
    // If we have search indicator data, show SearchIndicator with text content
    if (searchIndicatorData) {
      return (
        <div className="bot-response">
          <SearchIndicator message={searchIndicatorData} />
          {/* Show text content below the search indicator */}
          {(currentResponse?.text && currentResponse.text !== '' && !currentResponse.text.startsWith('[[LOADING_INDICATOR]]')) && (
            <div className="mt-2 content-after-search" style={{ fontFamily: 'var(--chat-font-family)' }}>
              <MarkdownComponent
                isStreaming={message?.isStreaming}
                darkMode={darkMode}
                chatTitle={chatTitle}
              >
                {currentResponse.text}
              </MarkdownComponent>
            </div>
          )}
        </div>
      );
    }

    // 🚀 UNIFIED LOADING SYSTEM - Check for other loading indicators
    const unifiedIndicator = getLoadingIndicator(message._id, darkMode, message);
    const isLoading = isMessageLoading(message._id);
    const hasRenderableText = Boolean(
      currentResponse?.text &&
      currentResponse.text.trim() !== '' &&
      !currentResponse.text.startsWith('[[LOADING_INDICATOR]]')
    );
    // When we have tool indicators (e.g. "Building artifact...", "Loaded skill"), show those instead of default loading
    const hasToolCallActivity =
      (currentResponse?.toolCallPositions && currentResponse.toolCallPositions.length > 0) ||
      (message?.toolCallPositions && message.toolCallPositions.length > 0) ||
      currentResponse?.toolCallsActive ||
      message?.toolCallsActive ||
      message?.hasToolCalls ||
      toolCallManager?.hasToolCalls(message._id);

    const hasAnalysisCondition = message?.isAnalyzing;
    const hasDeepResearchCondition = message?.isDeepResearch && message?.metadata?.stage && message?.metadata?.progress;
    
    // Only show debug for search-related messages
    if (message?.isSearching || message?.hasWebSearch || currentResponse?.isSearching || currentResponse?.hasWebSearch) {
      console.log('SEARCH DEBUG - message:', message._id, 'isSearching:', message?.isSearching || currentResponse?.isSearching, 'hasWebSearch:', message?.hasWebSearch || currentResponse?.hasWebSearch);
    }
    
    // Skip unified loading when we have tool indicators — show tool pills + synthesized text instead
    if (!hasToolCallActivity && unifiedIndicator && isLoading && (
      unifiedIndicator.component !== 'DefaultLoading' || !hasRenderableText
    )) {
      return (
        <div className="bot-response">
          <UnifiedLoadingIndicator indicatorConfig={unifiedIndicator} darkMode={darkMode} />
          {/* Show any existing text content below the indicator */}
          {(currentResponse?.text && !currentResponse.text.startsWith('[[LOADING_INDICATOR]]') && currentResponse.text !== 'Creating image') && (
            <div className="mt-2" style={{ fontFamily: 'var(--chat-font-family)' }}>
              <MarkdownComponent
                isStreaming={message?.isStreaming}
                darkMode={darkMode}
                chatTitle={chatTitle}
              >
                {currentResponse.text}
              </MarkdownComponent>
            </div>
          )}
        </div>
      );
    }
    
    
    // Check for special loading indicator marker - handle cases where there's content after the marker
    // When we have toolCallPositions or toolCallsActive (e.g. file gen), show tool indicator instead of dots (handled above by skipping unified indicator)
    if (currentResponse?.text?.startsWith('[[LOADING_INDICATOR]]') && !hasToolCallActivity) {
      const remainingText = currentResponse.text.replace('[[LOADING_INDICATOR]]', '').trim();
      
      if (remainingText) {
        // There's content after the loading indicator, render it normally
        return (
          <div className="bot-response">
            {/* Show document analysis indicator if analyzing */}
            {message?.isAnalyzing ? (
              <DocumentAnalysisIndicator message={message} />
            ) : 
            /* Show search indicator if searching or has searched (streaming or from database) OR has HubSpot functions */
            ((message?.isSearching || currentResponse?.hasSearched || currentResponse?.hasWebSearch) && (currentResponse?.searchStatus || currentResponse?.hasWebSearch)) || 
             (currentResponse?.hubspotFunction || currentResponse?.hasHubSpotFunction) ? (
              <SearchIndicator message={{...message, ...currentResponse}} />
            ) : null}
            
            {/* Show memory indicator if memory was updated */}
            {(message?.hasMemoryUpdate || currentResponse?.hasMemoryUpdate) && (
              <MemoryIndicator message={{...message, ...currentResponse}} />
            )}
            
            {/* Show tool call indicators if there are active or completed tool calls */}
            {/* Tool call indicators are now handled inline via processMarkdownWithInlineTools - no need to show above */}
            
            {/* Render message content with proper spacing after search/analysis */}
            <div className={message?.isSearching || message?.isAnalyzing ? 'content-after-search' : ''}>
              {(() => {
                // Check if we should use unified loading system
                const unifiedLoadingIndicator = getLoadingIndicator(message._id, darkMode);
                if (unifiedLoadingIndicator && isMessageLoading(message._id)) {
                  if (unifiedLoadingIndicator.component !== 'DefaultLoading' || !hasRenderableText) {
                    return <UnifiedLoadingIndicator indicatorConfig={unifiedLoadingIndicator} darkMode={darkMode} />;
                  }
                }
                
                // Check if we're showing "Querying SuperBucket..." or "SuperBucket Intelligence" text and use shimmer effect
                if ((remainingText === 'Querying SuperBucket...' || remainingText === 'SuperBucket Intelligence') && message?.isThinking) {
                  return <ShimmerText text={remainingText} darkMode={darkMode} />;
                }
                
                // Default to streaming markdown
                return (
                  <MarkdownComponent
                    isStreaming={message?.isStreaming}
                    darkMode={darkMode}
                  >
                    {remainingText}
                  </MarkdownComponent>
                );
              })()}
            </div>

            {/* Display SuperBucket skeleton */}
            {message?.showSuperbucketSkeleton && (
              <div>
                <div className="mt-2 p-3 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
                    <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                    <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Display image generation skeleton or generated image */}
            {message?.showImageSkeleton ? (
              <div>
                <ImageGenerationSkeleton 
                  optimalSize={message?.optimalImageSize || 'auto'}
                  darkMode={darkMode}
                />
              </div>
            ) : message?.generatedImage?.presignedUrl && (
              <div className="pb-4">
                <img 
                  src={imageError ? getProxiedImageUrl(message.generatedImage.presignedUrl) : message.generatedImage.presignedUrl}
                  alt={`Generated image: ${message.generatedImage.originalPrompt || 'AI generated image'}`}
                  className="chat-attachment-img-squircle-lg max-h-[300px] object-contain cursor-pointer"
                  onClick={() => {
                    const workingUrl = imageError ? getProxiedImageUrl(message.generatedImage.presignedUrl) : message.generatedImage.presignedUrl;
                    handleImageClick(
                      workingUrl,
                      `Generated image: ${message.generatedImage.originalPrompt || 'AI generated image'}`
                    );
                  }}
                  onError={(e) => {
                    if (!imageError) {
                      // First error - try the proxy URL
                      console.log('S3 URL failed, trying proxy for generated image:', message.generatedImage.fileName);
                      setImageError(true);
                    } else {
                      // Proxy also failed - show fallback
                      console.error('Both S3 URL and proxy failed for generated image:', message.generatedImage.fileName, message.generatedImage.presignedUrl);
                      e.target.style.display = 'none';
                      // Try to show a fallback message
                      const fallback = document.createElement('div');
                      fallback.className = 'text-gray-500 dark:text-[#444444] text-sm p-4 border border-gray-300 dark:border-[#444444] rounded-lg';
                      fallback.textContent = `Failed to load generated image: ${message.generatedImage.fileName}`;
                      e.target.parentNode.appendChild(fallback);
                    }
                  }}
                  onLoad={() => {
                    console.log('Successfully loaded generated image:', message.generatedImage.fileName, imageError ? '(via proxy)' : '(direct)');
                  }}
                />
              </div>
            )}
            
            {/* Display generated file - HTML charts open in artifact panel */}
            {message?.generatedFile && (
              supportsArtifactPanelPreview(message.generatedFile) && onOpenArtifact ? (
                <ArtifactViewCard
                  onView={() => onOpenArtifact({ messageId: message._id, chatId, generatedFile: message.generatedFile })}
                  darkMode={darkMode}
                />
              ) : message.generatedFile.format === 'html' ? (
                <HtmlChartArtifact
                  chatId={chatId}
                  messageId={message._id}
                  generatedFile={message.generatedFile}
                  darkMode={darkMode}
                />
              ) : (
              <div className="mt-3 p-4 border border-gray-200 dark:border-[#444444] rounded-2xl bg-gray-50 dark:bg-[#1a1a1a]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                      <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">
                        {message.generatedFile.format?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {message.generatedFile.fileName}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-[#888888]">
                        {(message.generatedFile.fileSize / 1024).toFixed(2)} KB
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.get(
                          `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${message._id}/download-file`,
                          {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob'
                          }
                        );
                        
                        // Create download link
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', message.generatedFile.fileName);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        console.error('Error downloading file:', error);
                        alert('Failed to download file. Please try again.');
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      } else {
        // Only the loading indicator, use the unified system
        const loadingIndicator = getLoadingIndicator(message._id, darkMode);
        return <UnifiedLoadingIndicator indicatorConfig={loadingIndicator} fallbackToDots={true} darkMode={darkMode} />;
      }
    }

    // Handle presentation messages
    if (message?.type === 'presentation' && message?.presentation) {
      return (
        <div className="mt-2">
          <PresentationCard 
            presentation={message.presentation}
            messageId={message._id}
            onRefresh={null} // Could implement retry functionality here
          />
        </div>
      );
    }

    // Handle AI presentation messages
    if (message?.type === 'ai-presentation' && message?.presentation) {
      return (
        <div className="mt-2">
          <AIPresentationCard 
            presentation={message.presentation}
            messageId={message._id}
            onRefresh={null} // Could implement retry functionality here
          />
        </div>
      );
    }
    
    // If we're searching and have highlighted content, render with HTML directly
    if (hasHighlights) {
      return (
        <div className={message?.sender === 'bot' ? 'bot-response' : ''}>
          {/* For search highlighting - use dangerouslySetInnerHTML */}
          <div 
            className="whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: highlightedContent }} 
          />
          
          {/* Display SuperBucket skeleton */}
          {message?.showSuperbucketSkeleton && (
            <div>
              <div className="mt-2 p-3 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
                  <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          )}
          
          {/* Display image generation skeleton or generated image */}
          {message?.showImageSkeleton ? (
            <div>
              <ImageGenerationSkeleton 
                optimalSize={message?.optimalImageSize || 'auto'}
                darkMode={darkMode}
              />
            </div>
          ) : message?.generatedImage?.presignedUrl && (
            <div className="mt-3">
              <img 
                src={imageError ? getProxiedImageUrl(message.generatedImage.presignedUrl) : message.generatedImage.presignedUrl}
                alt={`Generated image: ${message.generatedImage.originalPrompt || 'AI generated image'}`}
                className="chat-attachment-img-squircle-lg max-h-[300px] object-contain cursor-pointer"
                onClick={() => {
                  const workingUrl = imageError ? getProxiedImageUrl(message.generatedImage.presignedUrl) : message.generatedImage.presignedUrl;
                  handleImageClick(
                    workingUrl,
                    `Generated image: ${message.generatedImage.originalPrompt || 'AI generated image'}`
                  );
                }}
                onError={(e) => {
                  if (!imageError) {
                    // First error - try the proxy URL
                    console.log('S3 URL failed, trying proxy for generated image:', message.generatedImage.fileName);
                    setImageError(true);
                  } else {
                    // Proxy also failed - show fallback
                    console.error('Both S3 URL and proxy failed for generated image:', message.generatedImage.fileName, message.generatedImage.presignedUrl);
                    e.target.style.display = 'none';
                    // Try to show a fallback message
                    const fallback = document.createElement('div');
                    fallback.className = 'text-gray-500 dark:text-[#444444] text-sm p-4 border border-gray-300 dark:border-[#444444] rounded-lg';
                    fallback.textContent = `Failed to load generated image: ${message.generatedImage.fileName}`;
                    e.target.parentNode.appendChild(fallback);
                  }
                }}
                onLoad={() => {
                  console.log('Successfully loaded generated image:', message.generatedImage.fileName, imageError ? '(via proxy)' : '(direct)');
                }}
              />
            </div>
          )}
          
          {/* Display generated file - HTML charts open in artifact panel */}
          {message?.generatedFile && (
            supportsArtifactPanelPreview(message.generatedFile) && onOpenArtifact ? (
              <ArtifactViewCard
                onView={() => onOpenArtifact({ messageId: message._id, chatId, generatedFile: message.generatedFile })}
                darkMode={darkMode}
              />
            ) : message.generatedFile.format === 'html' ? (
              <HtmlChartArtifact
                chatId={chatId}
                messageId={message._id}
                generatedFile={message.generatedFile}
                darkMode={darkMode}
              />
            ) : (
              <div className="mt-3 p-4 border border-gray-200 dark:border-[#444444] rounded-2xl bg-gray-50 dark:bg-[#1a1a1a]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">
                      {message.generatedFile.format?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {message.generatedFile.fileName}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-[#888888]">
                      {(message.generatedFile.fileSize / 1024).toFixed(2)} KB
                    </div>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.get(
                        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${message._id}/download-file`,
                        {
                          headers: { Authorization: `Bearer ${token}` },
                          responseType: 'blob'
                        }
                      );
                      
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', message.generatedFile.fileName);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error('Error downloading file:', error);
                      alert('Failed to download file. Please try again.');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Download
                </button>
              </div>
            </div>
            )
          )}
        </div>
      );
    }

    // Handle legacy image messages (old format) - only for bot messages or when called from legacy handling
    if (message?.type === 'image' && message?.fileUrl) {
      return (
        <AuthenticatedImage
          src={message.fileUrl}
          alt={message.fileName || 'Uploaded image'}
          fileName={message.fileName}
        />
      );
    }
    
    // Handle legacy file messages (old format)
    if (message?.type === 'file' && message?.fileUrl) {
      const isPDF = isPDFFile(message.fileName);
      
      return (
        <div className="mt-2">
          {isPDF ? (
            <PDFFilePreview fileUrl={message.fileUrl} fileName={message.fileName} />
          ) : (
            <a 
              href={message.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 underline flex items-center"
              title={message.fileName}
            >
              {message.fileName}
            </a>
          )}
        </div>
      );
    }

    // If we have a markdown link to a file, extract and display it properly
    if (message?.text && hasFileLinks(message.text)) {
      const linkInfo = extractMarkdownLink(message.text);
      
      if (linkInfo) {
        const { fileName, fileUrl, fullMatch } = linkInfo;
        const isPDF = isPDFFile(fileName);
        
        const textBeforeFile = message.text.split(fullMatch)[0].trim();
        const textAfterFile = message.text.split(fullMatch)[1]?.trim();
        
        return (
          <div>
            {textBeforeFile && (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {textBeforeFile}
              </ReactMarkdown>
            )}
            
            <div className="my-3">
              {isPDF ? (
                <PDFFilePreview fileUrl={fileUrl} fileName={fileName} />
              ) : (
                <a 
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 underline flex items-center"
                  title={fileName}
                >
                  {fileName}
                </a>
              )}
            </div>
            
            {textAfterFile && (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {textAfterFile}
              </ReactMarkdown>
            )}
          </div>
        );
      }
    }

    // For user messages, use simple text rendering with Midus mention emphasis
    if (message?.sender === 'user') {
      return (
        <div className="whitespace-pre-wrap" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          {renderUserTextWithMentions(message?.text)}
        </div>
      );
    }

    // For bot messages, use clean markdown processing that respects LLM formatting
    return (
      <div className="bot-response">
        {/* All loading indicators are handled by the unified loading system */}
        
        {/* Show memory indicator if memory was updated AND message has real content */}
        {(message?.hasMemoryUpdate || currentResponse?.hasMemoryUpdate) && 
         !message?.isTemporary && 
         message?.text && 
         message?.text !== '[[LOADING_INDICATOR]]' && 
         message?.text.trim() !== '' && (
          <MemoryIndicator message={{...message, ...currentResponse}} />
        )}
        
        {/* Render message content with integrated tool indicators */}
        <div className={message?.isSearching || message?.isAnalyzing ? 'content-after-search' : ''}>
          {(currentResponse?.text || currentResponse?.generatedImage?.presignedUrl || currentResponse?.generatedFile || (currentResponse?.toolCallPositions && currentResponse.toolCallPositions.length > 0)) && (
            (() => {
              const hasToolCallPositions = currentResponse?.toolCallPositions && currentResponse.toolCallPositions.length > 0;
              const hasToolCalls = toolCallManager?.hasToolCalls(message._id);
              const toolCalls = toolCallManager?.getToolCalls(message._id) || [];
              
              if (hasToolCallPositions || hasToolCalls) {
                if (hasToolCallPositions) {
                  // Use inline tool renderer when we have tool call positions from backend
                  // Strip [[LOADING_INDICATOR]] placeholder but keep any content after it (e.g. post-tool message)
                  const rawText = currentResponse.text || '';
                  const textForTools = rawText === '[[LOADING_INDICATOR]]' ? '' : rawText.replace(/^\[\[LOADING_INDICATOR\]\]/, '').trim();
                  const segments = processMarkdownWithInlineTools(textForTools, currentResponse.toolCallPositions, message, darkMode);
                  return (
                    <div className="whitespace-pre-wrap flex flex-col">
                      {segments.map((segment, index) => {
                        if (typeof segment === 'string') {
                          // Render text segments with markdown - preserve streaming state for the last segment
                          const isLastSegment = index === segments.length - 1;
                          return (
                            <div key={index} className="w-full">
                              <MarkdownComponent
                                isStreaming={message?.isStreaming && isLastSegment}
                                darkMode={darkMode}
                                chatTitle={chatTitle}
                              >
                                {segment}
                              </MarkdownComponent>
                            </div>
                          );
                        } else {
                          // Render tool indicator - hug content
                          return <div key={index} className="self-start">{segment}</div>;
                        }
                      })}
                    </div>
                  );
                } else if (hasToolCalls) {
                  // Convert tool call manager data to inline positions format (use stored position from when tool started)
                  const FILE_GEN_TOOLS = ['create_html_artifact', 'generate_powerpoint_from_code', 'generate_html_chart', 'generate_word_document', 'generate_excel_spreadsheet', 'generate_csv_file', 'generate_pdf_document'];
                  const syntheticPositions = toolCalls.map((toolCall, index) => ({
                    position: toolCall.position ?? 0,
                    toolId: toolCall.id,
                    functionName: toolCall.toolName,
                    type: FILE_GEN_TOOLS.includes(toolCall.toolName) ? 'file_generation' : 'tool_call',
                    status: toolCall.status,
                    statusMessage: toolCall.statusMessage,
                    recordCount: toolCall.recordCount,
                    isComplete: toolCall.status === 'success' || toolCall.status === 'error',
                    result: toolCall.result,
                    error: toolCall.error
                  }));
                  
                  // Use inline tool renderer with synthetic positions (strip loading placeholder)
                  const syntheticText = (currentResponse.text || '').replace(/^\[\[LOADING_INDICATOR\]\]/, '').trim();
                  const segments = processMarkdownWithInlineTools(
                    syntheticText, 
                    syntheticPositions, 
                    {
                      ...message,
                      toolCalls: toolCalls // Pass tool calls for status checking
                    }, 
                    darkMode
                  );
                  
                  return (
                    <div className="whitespace-pre-wrap flex flex-col">
                      {segments.map((segment, index) => {
                        if (typeof segment === 'string') {
                          const isLastSegment = index === segments.length - 1;
                          return (
                            <div key={index} className="w-full">
                              <MarkdownComponent
                                isStreaming={message?.isStreaming && isLastSegment}
                                darkMode={darkMode}
                                chatTitle={chatTitle}
                              >
                                {segment}
                              </MarkdownComponent>
                            </div>
                          );
                        } else {
                          return <div key={index} className="self-start">{segment}</div>;
                        }
                      })}
                    </div>
                  );
                }
              } else {
                // Use normal markdown rendering when no tool positions
                // Check if we should use unified loading system for special states
                const unifiedLoadingIndicator = getLoadingIndicator(message._id, darkMode);
                if (unifiedLoadingIndicator && isMessageLoading(message._id)) {
                  return <UnifiedLoadingIndicator indicatorConfig={unifiedLoadingIndicator} darkMode={darkMode} />;
                }
                
                // Check if we're showing "Querying SuperBucket..." text and use shimmer effect
                if (currentResponse?.text === 'Querying SuperBucket...' && message?.isThinking) {
                  return <ShimmerText text={currentResponse.text} darkMode={darkMode} />;
                }
                
                // Only render streaming markdown if there's actual text content (not placeholder)
                const displayText = (currentResponse?.text || '').replace(/^\[\[LOADING_INDICATOR\]\]/, '').trim();
                if (displayText) {
                  return (
                    <MarkdownComponent
                      isStreaming={message?.isStreaming}
                      darkMode={darkMode}
                      chatTitle={chatTitle}
                    >
                      {displayText}
                    </MarkdownComponent>
                  );
                }
                
                // No text content, return null so images can be displayed below
                return null;
              }
            })()
          )}
          
          {/* Show tool indicators if there's no text content yet (for loading states) */}
          {(() => {
                         const hasNoText = !currentResponse?.text || currentResponse.text.trim() === '';
             const hasAnalysisCondition = message?.isAnalyzing;
             const hasSearchCondition = ((message?.isSearching || currentResponse?.hasSearched || currentResponse?.hasWebSearch) && (currentResponse?.searchStatus || currentResponse?.hasWebSearch)) || 
                                        (currentResponse?.hubspotFunction || currentResponse?.hasHubSpotFunction);
             const hasDeepResearchCondition = message?.isDeepResearch && message?.metadata?.stage && message?.metadata?.progress;
             const hasToolCallPositions = currentResponse?.toolCallPositions && currentResponse?.toolCallPositions?.length > 0;
             // During streaming, if we have HubSpot functions, never show fallback indicator
             const isStreamingHubSpotFunction = (message?.isStreaming || message?.isSearching) && (currentResponse?.hubspotFunction || currentResponse?.hasHubSpotFunction);
             
             const shouldShowFallbackIndicator = hasNoText && (hasAnalysisCondition || hasSearchCondition) && !hasToolCallPositions && !isStreamingHubSpotFunction;
            
            if (shouldShowFallbackIndicator) {
              if (hasAnalysisCondition) {
                return <DocumentAnalysisIndicator message={message} />;
              } else if (hasSearchCondition) {
                return <SearchIndicator message={{...message, ...currentResponse}} />;
              }
            }
            return null;
          })()}
        </div>

        {/* Display SuperBucket skeleton */}
        {message?.showSuperbucketSkeleton && (
          <div>
            <div className="mt-2 p-3 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
              </div>
              <div className="space-y-2">
                <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
                <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        )}
        
        {/* Display image generation skeleton or generated image */}
        {message?.showImageSkeleton ? (
          <div>
            <ImageGenerationSkeleton 
              optimalSize={message?.optimalImageSize || 'auto'}
              darkMode={darkMode}
            />
          </div>
        ) : currentResponse?.generatedImage?.presignedUrl && (
          <div className="pb-4">
            <img 
              src={currentResponseImageError ? getProxiedImageUrl(currentResponse.generatedImage.presignedUrl) : currentResponse.generatedImage.presignedUrl}
              alt={`Generated image: ${currentResponse.generatedImage.originalPrompt || 'AI generated image'}`}
              className="chat-attachment-img-squircle-lg max-h-[300px] object-contain cursor-pointer"
              onClick={() => {
                const workingUrl = currentResponseImageError ? getProxiedImageUrl(currentResponse.generatedImage.presignedUrl) : currentResponse.generatedImage.presignedUrl;
                handleImageClick(
                  workingUrl,
                  `Generated image: ${currentResponse.generatedImage.originalPrompt || 'AI generated image'}`
                );
              }}
              onError={(e) => {
                if (!currentResponseImageError) {
                  // First error - try the proxy URL
                  console.log('S3 URL failed, trying proxy for current response generated image:', currentResponse.generatedImage.fileName);
                  setCurrentResponseImageError(true);
                } else {
                  // Proxy also failed - show fallback
                  console.error('Both S3 URL and proxy failed for current response generated image:', currentResponse.generatedImage.fileName, currentResponse.generatedImage.presignedUrl);
                  e.target.style.display = 'none';
                  // Try to show a fallback message
                  const fallback = document.createElement('div');
                  fallback.className = 'text-gray-500 dark:text-[#444444] text-sm p-4 border border-gray-300 dark:border-[#444444] rounded-lg';
                  fallback.textContent = `Failed to load generated image: ${currentResponse.generatedImage.fileName}`;
                  e.target.parentNode.appendChild(fallback);
                }
              }}
              onLoad={() => {
                console.log('Successfully loaded current response generated image:', currentResponse.generatedImage.fileName, currentResponseImageError ? '(via proxy)' : '(direct)');
              }}
            />
          </div>
        )}
        
        {/* Display generated file - HTML charts open in artifact panel */}
        {currentResponse?.generatedFile && (
          supportsArtifactPanelPreview(currentResponse.generatedFile) && onOpenArtifact ? (
            <ArtifactViewCard
              onView={() => onOpenArtifact({ messageId: message._id, chatId, generatedFile: currentResponse.generatedFile })}
              darkMode={darkMode}
            />
          ) : currentResponse.generatedFile.format === 'html' ? (
            <HtmlChartArtifact
              chatId={chatId}
              messageId={message._id}
              generatedFile={currentResponse.generatedFile}
              darkMode={darkMode}
            />
          ) : (
            <div className="mt-3 p-4 border border-gray-200 dark:border-[#444444] rounded-2xl bg-gray-50 dark:bg-[#1a1a1a]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">
                      {currentResponse.generatedFile.format?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {currentResponse.generatedFile.fileName}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-[#888888]">
                      {(currentResponse.generatedFile.fileSize / 1024).toFixed(2)} KB
                    </div>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.get(
                        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${message._id}/download-file`,
                        {
                          headers: { Authorization: `Bearer ${token}` },
                          responseType: 'blob'
                        }
                      );
                      
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', currentResponse.generatedFile.fileName);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error('Error downloading file:', error);
                      alert('Failed to download file. Please try again.');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Download
                </button>
              </div>
            </div>
          )
        )}
      </div>
    );
  };

  const renderFeedbackButtons = () => (
    <>
      {/* Response switcher when multiple responses exist */}
      {hasMultipleResponses && (
        <div className="flex items-center gap-0.5 mr-2">
          <button
            className={`w-6 h-6 md:w-5 md:h-5 flex items-center justify-center rounded-full transition-colors duration-200 ${
              activeResponseIndex > 0 ? 'text-gray-400 hover:text-black' : 'text-gray-300 cursor-not-allowed'
            }`}
            style={activeResponseIndex > 0 ? { color: darkMode ? '#999999' : undefined } : { color: darkMode ? '#666666' : '#d1d5db' }}
            onClick={handlePreviousResponse}
            disabled={activeResponseIndex <= 0}
            title="Previous response"
          >
            <svg className="h-4 w-4 md:h-3.5 md:w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-[#282828] rounded-lg text-gray-600 dark:text-[#999999]">
            {activeResponseIndex + 1} / {message.responses.length}
          </span>
          
          <button
            className={`w-6 h-6 md:w-5 md:h-5 flex items-center justify-center rounded-full transition-colors duration-200 ${
              activeResponseIndex < message.responses.length - 1 ? 'text-gray-400 hover:text-black' : 'text-gray-300 cursor-not-allowed'
            }`}
            style={activeResponseIndex < message.responses.length - 1 ? { color: darkMode ? '#999999' : undefined } : { color: darkMode ? '#666666' : '#d1d5db' }}
            onClick={handleNextResponse}
            disabled={activeResponseIndex >= message.responses.length - 1}
            title="Next response"
          >
            <svg className="h-4 w-4 md:h-3.5 md:w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Thumbs Up Button - hide when thumbs down is selected */}
      {feedback !== 'down' && (
        <button
          disabled={!canSubmitFeedback}
          className={`w-7 h-7 flex items-center justify-center rounded-[10px] transition-all duration-200 max-[1200px]:w-6 max-[1200px]:h-6 max-[900px]:w-5 max-[900px]:h-5 [@media(max-height:800px)]:w-6 [@media(max-height:800px)]:h-6 [@media(max-height:700px)]:w-5 [@media(max-height:700px)]:h-5 ${
            feedback === 'up' 
              ? 'bg-[#f4f4f5] dark:bg-[#1f1f23]' 
              : 'hover:bg-gray-100 dark:hover:bg-[#282828]'
          } ${!canSubmitFeedback ? 'cursor-not-allowed opacity-45' : ''}`}
          style={{ 
            color: feedback === 'up' 
              ? (darkMode ? '#a1a1aa' : '#6b7280') 
              : (darkMode ? '#a1a1aa' : '#6b7280')
          }}
          onClick={() => handleFeedback(true)}
          title={canSubmitFeedback ? 'Helpful response' : 'Feedback available once the response saves'}
          aria-disabled={!canSubmitFeedback}
        >
          <ThumbsUpIcon size={16} strokeWidth={1.5} className="max-[1200px]:w-3.5 max-[1200px]:h-3.5 max-[900px]:w-3 max-[900px]:h-3 [@media(max-height:800px)]:w-3.5 [@media(max-height:800px)]:h-3.5 [@media(max-height:700px)]:w-3 [@media(max-height:700px)]:h-3" />
        </button>
      )}
      
      {/* Thumbs Down Button - hide when thumbs up is selected */}
      {feedback !== 'up' && (
        <button
          disabled={!canSubmitFeedback}
          className={`w-7 h-7 flex items-center justify-center rounded-[10px] transition-all duration-200 max-[1200px]:w-6 max-[1200px]:h-6 max-[900px]:w-5 max-[900px]:h-5 [@media(max-height:800px)]:w-6 [@media(max-height:800px)]:h-6 [@media(max-height:700px)]:w-5 [@media(max-height:700px)]:h-5 ${
            feedback === 'down' 
              ? 'bg-[#f4f4f5] dark:bg-[#1f1f23]' 
              : 'hover:bg-gray-100 dark:hover:bg-[#282828]'
          } ${!canSubmitFeedback ? 'cursor-not-allowed opacity-45' : ''}`}
          style={{ 
            color: feedback === 'down' 
              ? (darkMode ? '#a1a1aa' : '#6b7280') 
              : (darkMode ? '#a1a1aa' : '#6b7280')
          }}
          onClick={() => handleFeedback(false)}
          title={canSubmitFeedback ? 'Unhelpful response' : 'Feedback available once the response saves'}
          aria-disabled={!canSubmitFeedback}
        >
          <ThumbsDownIcon size={16} strokeWidth={1.5} className="max-[1200px]:w-3.5 max-[1200px]:h-3.5 max-[900px]:w-3 max-[900px]:h-3 [@media(max-height:800px)]:w-3.5 [@media(max-height:800px)]:h-3.5 [@media(max-height:700px)]:w-3 [@media(max-height:700px)]:h-3" />
        </button>
      )}
    </>
  );

  return (
    <>
      <div
        className={`flex flex-col ${
          message?.sender === "user"
            ? `${compactUserMessage ? 'pb-1' : 'pb-4'} ${resolvedIsOwnMessage ? 'items-end' : 'items-start'}`
            : "pb-2 items-start"
        } ${isActiveSearchResult ? 'scroll-mt-32' : ''}`}
        id={isActiveSearchResult ? `message-${message?._id}` : undefined}
      >
        {message?.sender === "bot" ? (
          <div className="flex items-start w-full">
            {/*
            <div className="chat-logo-avatar flex-shrink-0 pr-3 hidden sm:block">
              <div className="w-[26px] h-[26px] rounded-full bg-[#F9FAFB] dark:bg-[#282828] border border-[#E5E7EB] dark:border-[#444444] flex items-center justify-center max-[1200px]:w-[22px] max-[1200px]:h-[22px] max-[900px]:w-[20px] max-[900px]:h-[20px] [@media(max-height:800px)]:w-[22px] [@media(max-height:800px)]:h-[22px] [@media(max-height:700px)]:w-[20px] [@media(max-height:700px)]:h-[20px]">
                <img src={logoUrl} alt="Midus" className="w-[18px] h-[18px] max-[1200px]:w-[15px] max-[1200px]:h-[15px] max-[900px]:w-[14px] max-[900px]:h-[14px] [@media(max-height:800px)]:w-[15px] [@media(max-height:800px)]:h-[15px] [@media(max-height:700px)]:w-[14px] [@media(max-height:700px)]:h-[14px]" />
              </div>
            </div>
            */}
            <div 
              className="flex-1 whitespace-pre-wrap -mb-4 relative group overflow-visble"
              style={{ backgroundColor: "transparent", fontFamily: 'var(--chat-font-family)' }}
            >
              {/* was: pl-2 sm:pl-0 (pl-2 commented out with no logo circle experiment) */}
              {editingMessageIndex === message?._id ? (
                <div className="mt-2">
                  <input
                    value={editInput}
                    onChange={(e) => setEditInput(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleEdit(message?._id, editInput)
                    }
                    className="bg-button-bg rounded-full px-4 py-2 text-black w-full"
                    style={{ fontFamily: 'var(--chat-font-family)' }}
                  />
                  <div className="flex gap-3 mt-2">
                    <button
                      onClick={() => handleEdit(message?._id, editInput)}
                      className="text-sm text-black dark:text-white"
                      style={{ 
                        '--hover-color': darkMode ? '#999999' : '#d1d5db' 
                      }}
                      onMouseEnter={e => e.target.style.color = darkMode ? '#999999' : '#d1d5db'}
                      onMouseLeave={e => e.target.style.color = darkMode ? '#ffffff' : '#000000'}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingMessageIndex(null)}
                      className="text-sm text-gray-400 hover:text-black dark:hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  ref={messageContentRef}
                  className="message-fade-in overflow-hidden" 
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                >
                  {renderContent()}
                </div>
              )}
              {message?.sender === "bot" && !message.isTemporary && (!message.isStreaming || message.isContentStreamingComplete) && !message.isThinking && !isMessageLoading(message._id) && (
                <div className="flex flex-col gap-2 mb-6 mt-4 ml-[-6px] max-[1200px]:gap-1.5 max-[1200px]:mt-3 max-[1200px]:mb-4 max-[900px]:gap-1 max-[900px]:mt-2 max-[900px]:mb-3 [@media(max-height:800px)]:gap-1.5 [@media(max-height:800px)]:mt-3 [@media(max-height:800px)]:mb-4 [@media(max-height:700px)]:gap-1 [@media(max-height:700px)]:mt-2 [@media(max-height:700px)]:mb-3">
                  {/* View Memory Button - shows when user asks memory-related questions */}
                  <ViewMemoryButton message={message} messages={messages} />
                  
                  {showContinueButton && (
                    <div className="mb-1">
                      <button
                        onClick={handleContinueResponse}
                        className="text-gray-600 hover:text-black dark:hover:text-white transition-colors text-xs px-2 py-0.5 border border-gray-400 dark:border-[#282828] rounded-md bg-gray-100 dark:bg-[#282828]"
                        style={{ color: darkMode ? '#999999' : undefined }}
                        title="Continue the response"
                      >
                        Continue?
                      </button>
                    </div>
                  )}
                  <div className={`flex items-center gap-0.5 pl-0 -mt-1 opacity-100 md:opacity-0 ${
                    isCopied ? 'md:opacity-100' : 'md:group-hover:opacity-100'
                  } transition-opacity`}>
                    <button
                      onClick={() => handleLocalCopy(currentResponse.text)}
                      className="w-7 h-7 flex items-center justify-center rounded-[10px] hover:bg-gray-100 dark:hover:bg-[#282828] transition-all duration-200 max-[1200px]:w-6 max-[1200px]:h-6 max-[900px]:w-5 max-[900px]:h-5 [@media(max-height:800px)]:w-6 [@media(max-height:800px)]:h-6 [@media(max-height:700px)]:w-5 [@media(max-height:700px)]:h-5"
                      style={{ color: darkMode ? '#a1a1aa' : '#6b7280' }}
                      title="Copy to clipboard"
                    >
                      <div className="relative w-4 h-4 max-[1200px]:w-3.5 max-[1200px]:h-3.5 max-[900px]:w-3 max-[900px]:h-3 [@media(max-height:800px)]:w-3.5 [@media(max-height:800px)]:h-3.5 [@media(max-height:700px)]:w-3 [@media(max-height:700px)]:h-3">
                        <div className={`absolute inset-0 transition-all duration-200 ease-in-out ${
                          isCopied ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                        }`}>
                          <CopyIcon size={16} strokeWidth={1.8} className="max-[1200px]:w-3.5 max-[1200px]:h-3.5 max-[900px]:w-3 max-[900px]:h-3 [@media(max-height:800px)]:w-3.5 [@media(max-height:800px)]:h-3.5 [@media(max-height:700px)]:w-3 [@media(max-height:700px)]:h-3" />
                        </div>
                        <div className={`absolute inset-0 transition-all duration-200 ease-in-out ${
                          isCopied ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                        }`}>
                          <LuCircleCheck className="h-4 w-4 max-[1200px]:h-3.5 max-[1200px]:w-3.5 max-[900px]:h-3 max-[900px]:w-3 [@media(max-height:800px)]:h-3.5 [@media(max-height:800px)]:w-3.5 [@media(max-height:700px)]:h-3 [@media(max-height:700px)]:w-3" style={{ color: darkMode ? '#a1a1aa' : '#6b7280' }} />
                        </div>
                      </div>
                    </button>
                    {renderFeedbackButtons()}
                    <button 
                      className="w-7 h-7 flex items-center justify-center rounded-[10px] hover:bg-gray-100 dark:hover:bg-[#282828] transition-all duration-200 max-[1200px]:w-6 max-[1200px]:h-6 max-[900px]:w-5 max-[900px]:h-5 [@media(max-height:800px)]:w-6 [@media(max-height:800px)]:h-6 [@media(max-height:700px)]:w-5 [@media(max-height:700px)]:h-5"
                      style={{ color: darkMode ? '#a1a1aa' : '#6b7280' }}
                      onClick={() => {
                        console.log('🔄 Regenerate button clicked in ChatMessage');
                        handleReprompt(message);
                      }}
                      title="Regenerate response"
                    >
                      <LuRefreshCcw className="h-4 w-4 max-[1200px]:h-3.5 max-[1200px]:w-3.5 max-[900px]:h-3 max-[900px]:w-3 [@media(max-height:800px)]:h-3.5 [@media(max-height:800px)]:w-3.5 [@media(max-height:700px)]:h-3 [@media(max-height:700px)]:w-3" />
                    </button>
                  </div>
                  
                  {/* Negative Feedback Form */}
                  <FeedbackForm
                    showForm={showNegativeFeedbackForm}
                    feedbackText={feedbackText}
                    setFeedbackText={setFeedbackText}
                    feedbackScreenshot={feedbackScreenshot}
                    isSubmitting={isSubmittingFeedback}
                    isTakingScreenshot={isTakingScreenshot}
                    onSubmit={handleDetailedFeedbackSubmit}
                    onCancel={handleFeedbackCancel}
                    onClose={handleCloseWithoutSubmit}
                    onScreenshotUpload={handleScreenshotUpload}
                    onTakeScreenshot={handleTakeScreenshot}
                    feedbackFormRef={feedbackFormRef}
                    messageId={stableMessageId || message._id}
                  />
                </div>
              )}
              {message.edited && (
                <span className="text-xs text-gray-400 ml-2">(edited)</span>
              )}
            </div>
          </div>
        ) : (
          // For user messages with attachments, we need to separate text and images
          (message?.attachments && message.attachments.length > 0) ? (
          <div className={`w-full flex flex-col ${resolvedIsOwnMessage ? 'items-end' : 'items-start'} space-y-2`}>
              {/* Render user profile header for project chats */}
              <UserProfileHeader 
                userInfo={getUserInfo(message?.userId, projectData, currentUser)} 
                isProjectChat={isProjectChat} 
                hideAvatar={hideUserHeader}
              />
              
              {/* Render attachments - grid layout for multiple images/files (matches mobile) */}
              {(() => {
                const attachments = message.attachments || [];
                const imageAttachments = attachments.filter(a => a.type === 'image' || isImageFile(a.fileName));
                const fileAttachments = attachments.filter(a => a.type !== 'image' && !isImageFile(a.fileName));
                const hasMultipleImages = imageAttachments.length > 1;
                const hasMultipleFiles = fileAttachments.length > 1;
                const n = imageAttachments.length;
                const imgCols = n === 2 ? 2 : n === 4 ? 2 : n === 6 ? 3 : n === 8 ? 4 : n === 10 ? 5 : Math.min(n, 4);
                const imgMaxW = n <= 4 ? 360 : n <= 6 ? 420 : 500;
                const imageGrid = hasMultipleImages ? (
                  <div
                    className={`image-attachment-grid grid gap-2 ${resolvedIsOwnMessage ? 'justify-items-end' : 'justify-items-start'} ${n >= 8 ? 'image-grid-cols-4-5' : ''}`}
                    style={{
                      '--img-cols': imgCols,
                      gridTemplateColumns: 'repeat(var(--img-cols), minmax(0, 1fr))',
                      maxWidth: `min(100%, ${imgMaxW}px)`,
                      direction: resolvedIsOwnMessage ? 'rtl' : 'ltr'
                    }}
                  >
                    {imageAttachments.map((attachment, index) => (
                      <div key={attachment.s3Key || `${attachment.fileName}-${index}`} className="min-w-0 overflow-hidden chat-attachment-img-squircle">
                        <FileAttachment
                          attachment={attachment}
                          isProjectChat={isProjectChat}
                          userId={message?.userId}
                          onImageClick={handleImageClick}
                          isOwnMessage={resolvedIsOwnMessage}
                          variant="grid"
                        />
                      </div>
                    ))}
                  </div>
                ) : null;
                return (
                  <div className={`flex flex-col ${resolvedIsOwnMessage ? 'items-end' : 'items-start'} gap-2 mb-1`}>
                    {imageGrid}
                    {/* Single image: render full-size */}
                    {!hasMultipleImages && imageAttachments.length === 1 && (
                      <FileAttachment
                        key={imageAttachments[0].s3Key || imageAttachments[0].fileName}
                        attachment={imageAttachments[0]}
                        isProjectChat={isProjectChat}
                        userId={message?.userId}
                        onImageClick={handleImageClick}
                        isOwnMessage={resolvedIsOwnMessage}
                      />
                    )}
                    {/* Single file (no images or single image case): render as chip */}
                    {!hasMultipleFiles && fileAttachments.length === 1 && (
                      <FileAttachment
                        key={fileAttachments[0].s3Key || fileAttachments[0].fileName}
                        attachment={fileAttachments[0]}
                        isProjectChat={isProjectChat}
                        userId={message?.userId}
                        onImageClick={handleImageClick}
                        isOwnMessage={resolvedIsOwnMessage}
                      />
                    )}
                    {/* Multiple files: grid */}
                    {hasMultipleFiles && (
                      <div className={`flex flex-row flex-wrap gap-2 ${resolvedIsOwnMessage ? 'justify-end' : 'justify-start'}`}>
                        {fileAttachments.map((attachment, index) => (
                          <FileAttachment
                            key={attachment.s3Key || `${attachment.fileName}-${index}`}
                            attachment={attachment}
                            isProjectChat={isProjectChat}
                            userId={message?.userId}
                            onImageClick={handleImageClick}
                            isOwnMessage={resolvedIsOwnMessage}
                            variant="grid"
                          />
                        ))}
                      </div>
                    )}
                    {/* Single file below multiple images */}
                    {hasMultipleImages && fileAttachments.length === 1 && (
                      <FileAttachment
                        key={fileAttachments[0].s3Key || fileAttachments[0].fileName}
                        attachment={fileAttachments[0]}
                        isProjectChat={isProjectChat}
                        userId={message?.userId}
                        onImageClick={handleImageClick}
                        isOwnMessage={resolvedIsOwnMessage}
                      />
                    )}
                  </div>
                );
              })()}
              
              {/* Render text in proper user message bubble below the image if present */}
              {message.text && message.text.trim() && (
                <div
                  className="user-message px-4 py-2.5 relative group overflow-hidden bg-[#F9FAFB] dark:bg-[#282828] border border-[#E5E7EB] dark:border-[#444444] text-black dark:text-white whitespace-pre-wrap"
                  style={{ 
                    fontFamily: 'var(--chat-font-family)',
                    alignSelf: resolvedIsOwnMessage ? 'flex-end' : 'flex-start',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {renderUserTextWithMentions(message.text)}
                </div>
              )}
            </div>
          ) : (
            // Special handling for legacy image messages only - skip the container
            (message?.type === 'image' && !message?.attachments) ? (
              <div className={`${resolvedIsOwnMessage ? 'text-right' : 'text-left'}`}>
                {renderContent()}
              </div>
            ) : (
              // Regular user message with grey container
              <div className={`w-full flex flex-col ${resolvedIsOwnMessage ? 'items-end' : 'items-start'}`}>
                {/* Render user profile header for project chats */}
                <UserProfileHeader 
                  userInfo={getUserInfo(message?.userId, projectData, currentUser)} 
                  isProjectChat={isProjectChat} 
                  hideAvatar={hideUserHeader}
                />
                
                <div
                  className={`user-message px-4 py-2.5 relative group overflow-hidden bg-[#F9FAFB] dark:bg-[#282828] border border-[#E5E7EB] dark:border-[#444444] text-black dark:text-white ${editingMessageIndex !== message?._id && !hasHighlights ? 'whitespace-pre-wrap' : ''}`}
                  style={{ 
                    fontFamily: 'var(--chat-font-family)',
                    alignSelf: resolvedIsOwnMessage ? 'flex-end' : 'flex-start',
                    ...(editingMessageIndex !== message?._id && !hasHighlights ? { wordBreak: 'break-word', overflowWrap: 'break-word' } : {})
                  }}
                >
                  {editingMessageIndex === message?._id ? (
                    <div className="mt-2">
                      <input
                        value={editInput}
                        onChange={(e) => setEditInput(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === "Enter" && handleEdit(message?._id, editInput)
                        }
                        className="bg-button-bg rounded-full px-4 py-2 text-black w-full"
                        style={{ fontFamily: 'var(--chat-font-family)' }}
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleEdit(message?._id, editInput)}
                          className="text-sm text-black dark:text-white hover:text-gray-300"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingMessageIndex(null)}
                          className="text-gray-400 hover:text-black dark:hover:text-white"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : hasHighlights ? (
                    <div 
                      className="whitespace-pre-wrap"
                      style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      dangerouslySetInnerHTML={{ __html: highlightedContent }} 
                    />
                  ) : (
                    <>
                      {renderUserTextWithMentions(message?.text)}
                      {message?.edited && (
                        <span className="text-xs text-gray-400 ml-2">(edited)</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            )
          )
        )}
        {/* Hover controls for all user messages except legacy image messages without attachments */}
        {message?.sender === "user" &&
          !(message?.type === 'image' && !message?.attachments) &&
          !message?.pending &&
          !editingMessageIndex && (
            <div className="absolute top-0 right-0 bg-gradient-to-l from-[#f2f2f2] via-[#f2f2f2] dark:from-[#282828] dark:via-[#282828] pb-2 pt-1 pl-4 pr-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <div className="flex space-x-1">
                <button
                  onClick={() => handleLocalCopy(message?.text || '')}
                  className="w-7 h-7 flex items-center justify-center rounded-[10px] hover:bg-gray-100 dark:hover:bg-[#282828] transition-all duration-200"
                  style={{ color: darkMode ? '#a1a1aa' : '#6b7280' }}
                  title="Copy to clipboard"
                >
                  <div className="relative w-4 h-4">
                    <div className={`absolute inset-0 transition-all duration-200 ease-in-out ${
                      isCopied ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                    }`}>
                      <CopyIcon size={16} strokeWidth={1.6} />
                    </div>
                    <div className={`absolute inset-0 transition-all duration-200 ease-in-out ${
                      isCopied ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                    }`}>
                      <LuCircleCheck className="h-4 w-4" style={{ color: darkMode ? '#a1a1aa' : '#6b7280' }} />
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setEditingMessageIndex(message?._id);
                    setEditInput(message?.text || '');
                  }}
                  className="text-gray-400 hover:text-black dark:hover:text-white transition-colors duration-200"
                  title="Edit message"
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
      </div>
      {showImageModal && (
        <ImageModal
          isOpen={showImageModal}
          onClose={handleCloseImageModal}
          imageUrl={selectedImageUrl}
          imageAlt={selectedImageAlt}
        />
      )}
    </>
  );
};

export default ChatMessage;